<template>
  <spinner v-if="loading"></spinner>
  <section class="my-6 text-center" v-if="!loading && !listExists">
    <h2 class="h3">{{ noListMessage }}</h2>
    <button
      type="button"
      class="btn text-decoration-underline mt-4"
      @click="closeList"
    >
      Til oversigten
    </button>
  </section>
  <section class="single-wishlist" v-if="!loading && listExists">
    <div class="bg__darkblue-medium">
      <div class="d-flex justify-content-between">
        <button type="button" class="btn" @click="closeList">
          <span class="icon-arrow_back"></span>
        </button>
        <div>
          <button type="button" class="btn" @click="showShare">
            <span class="icon-share2"></span>
          </button>
          <button type="button" class="btn" @click="showSettings">
            <span class="icon-settings"></span>
          </button>
        </div>
      </div>
    </div>
    <div>
      <h1>
        {{ wishlist.name }}
        <small v-if="favoriteText">{{ favoriteText }}</small>
      </h1>
      <button
        type="button"
        class="btn text-decoration-underline mt-4"
        @click="closeList"
        v-if="!wishlist.documents.length"
      >
        Til oversigten
      </button>
      <template v-for="(document, index) in wishlist.documents" :key="index">
        <article v-if="document.documentTypeAlias === 'PageTravel'">
          <div class="ratio ratio-16x9">
            <picture>
              <img
                class="img-fit"
                :src="document.imageWide"
                :alt="document.imageAltText"
              />
            </picture>
            <button
              type="button"
              :class="['btn add-favorite', { 'in-list': document.inlist }]"
              :title="[
                {
                  'Fjern rejse': document.inlist,
                  'Tilføj rejse': !document.inlist,
                },
              ]"
              @click="toggleDocument(document)"
            ></button>
          </div>
          <div>
            <span class="h6 text-secondary">Rejser</span>
            <span class="fw-bold d-block">{{ document.label }}</span>
            <h2>{{ document.title }}</h2>
            <p>{{ document.numberOfDays }} dage fra {{ document.fromPrice }}</p>
            <a :href="document.url">Se rejse</a>
          </div>
        </article>

        <article v-if="document.documentTypeAlias === 'PageHotel'">
          <div class="ratio ratio-16x9">
            <picture>
              <img
                class="img-fit"
                :src="document.imageWide"
                :alt="document.imageAltText"
              />
            </picture>
            <button
              type="button"
              :class="['btn add-favorite', { 'in-list': document.inlist }]"
              :title="[
                {
                  'Fjern hotel': document.inlist,
                  'Tilføj hotel': !document.inlist,
                },
              ]"
              @click="toggleDocument(document)"
            ></button>
          </div>
          <div>
            <span class="h6 text-secondary">Hoteller</span>
            <span class="fw-bold d-block">{{ document.label }}</span>
            <h2>{{ document.title }}</h2>
            <div class="star-container" v-if="document.showStars">
              <span
                class="icon-star"
                v-for="(star, index) in document.stars"
                :key="index"
              ></span>
              <span class="icon-plus" v-if="document.addPlusSignToStars"></span>
            </div>
            <a :href="document.url">Se hotel</a>
          </div>
        </article>

        <article v-if="document.documentTypeAlias === 'PageExperience'">
          <div class="ratio ratio-16x9">
            <picture>
              <img
                class="img-fit"
                :src="document.imageWide"
                :alt="document.imageAltText"
              />
            </picture>
            <button
              type="button"
              :class="['btn add-favorite', { 'in-list': document.inlist }]"
              :title="[
                {
                  'Fjern oplevelse': document.inlist,
                  'Tilføj oplevelse': !document.inlist,
                },
              ]"
              @click="toggleDocument(document)"
            ></button>
          </div>
          <div>
            <span class="h6 text-secondary">Oplevelser</span>
            <span class="fw-bold d-block">{{ document.label }}</span>
            <h2>{{ document.title }}</h2>
            <a :href="document.url">Se oplevelse</a>
          </div>
        </article>

        <article v-if="document.documentTypeAlias === 'PageBlog'">
          <div class="ratio ratio-16x9">
            <picture>
              <img
                class="img-fit"
                :src="document.imageWide"
                :alt="document.imageAltText"
              />
            </picture>
            <button
              type="button"
              :class="['btn add-favorite', { 'in-list': document.inlist }]"
              :title="[
                {
                  'Fjern blogindlæg': document.inlist,
                  'Tilføj blogindlæg': !document.inlist,
                },
              ]"
              @click="toggleDocument(document)"
            ></button>
          </div>
          <div>
            <span class="h6 text-secondary">Blogindlæg</span>
            <span class="fw-bold d-block">{{ document.label }}</span>
            <h2>{{ document.title }}</h2>
            <a :href="document.url">Se krydstogt</a>
          </div>
        </article>

        <article v-if="document.documentTypeAlias === 'PageCruise'">
          <div class="ratio ratio-16x9">
            <picture>
              <img
                class="img-fit"
                :src="document.image"
                :alt="document.imageAltText"
              />
            </picture>
            <button
              type="button"
              :class="['btn add-favorite', { 'in-list': document.inlist }]"
              :title="[
                {
                  'Fjern krydstogt': document.inlist,
                  'Tilføj krydstogt': !document.inlist,
                },
              ]"
              @click="toggleDocument(document)"
            ></button>
          </div>
          <div>
            <span class="h6 text-secondary">Krydstogter</span>
            <span class="fw-bold d-block">{{ document.label }}</span>
            <h2>{{ document.title }}</h2>
            <a :href="document.url">Se krydstogt</a>
          </div>
        </article>
      </template>
    </div>
  </section>
  <div role="alert" :class="[{ show: documentToggled }, 'favorite-info-box']">
    {{ wishlistInfo }}
  </div>
  <settings-wishlist-modal
    :wishlist="wishlist"
    v-if="showSettingsWishlistModal"
  />
  <share-wishlist-modal :wishlist="wishlist" v-if="showShareWishlistModal" />
</template>
<script>
import Spinner from "./Spinner.vue";
import SettingsWishlistModal from "./Modals/SettingsWishlistModal.vue";
import ShareWishlistModal from "./Modals/ShareWislistModal.vue";
export default {
  name: "SingleWishlist",
  props: ["wishlistId"],
  components: {
    Spinner,
    SettingsWishlistModal,
    ShareWishlistModal,
  },
  data() {
    return {
      loading: true,
      wishlist: undefined,
      showSettingsWishlistModal: false,
      showShareWishlistModal: false,
      documentToggled: false,
      wishlistInfo: "",
      listExists: true,
      noListMessage: "",
      favoriteCount: 0,
      infoTimeout: null
    };
  },
  computed: {
    favoriteText() {
      if (this.favoriteCount === 0) return "Du har ikke gemt nogen favoritter";

      return this.favoriteCount > 1
        ? "Du har gemt " + this.favoriteCount + " favoritter"
        : "Du har gemt 1 favorit";
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    init: function () {
      window.scrollTo({ top: 0, behavior: "smooth" });
      this.$store
        .dispatch("getSingleWishlist", this.wishlistId)
        .then((result) => {
          if (result.status) {
            this.listExists = false;
            this.noListMessage = result.data.Message;
            this.loading = false;
          } else if (result.id) {
            result.documents = result.documents.map((obj) => ({
              ...obj,
              inlist: true,
            })); // Need inlist property to toggle heart in visual list
            this.favoriteCount = result.documents.length;
            this.wishlist = result;
            this.loading = false;
          }
        });
    },
    toggleDocument: function (document) {
      let params = { listId: this.wishlist.id, documentId: document.id };
      if (document.inlist) {
        this.$store.dispatch("deleteDocumentFromWishlist", params).then(() => {
          if (this.favoriteCount > 0) this.favoriteCount--;
          document.inlist = false;
          this.wishlistInfo =
            document.title + " fjernet fra ønskelisten " + this.wishlist.name;
          this.documentToggled = true;
          clearTimeout(this.infoTimeout);
          this.infoTimeout = setTimeout(() => { this.documentToggled = false; }, 2000);
        });
      } else {
        this.$store.dispatch("addDocumentToWishlist", params).then(() => {
          this.favoriteCount++;
          document.inlist = true;
          this.wishlistInfo =
            document.title + " tilføjet til ønskelisten " + this.wishlist.name;
          this.documentToggled = true;
          clearTimeout(this.infoTimeout);
          this.infoTimeout = setTimeout(() => { this.documentToggled = false; }, 2000);
        });
      }
    },
    closeList: function () {
      window.location.href = window.location.href.split("?")[0];
      this.$parent.closeSingleList();
    },
    showSettings: function () {
      this.showSettingsWishlistModal = true;
    },
    hideSettings: function () {
      this.showSettingsWishlistModal = false;
    },
    showShare: function () {
      this.showShareWishlistModal = true;
    },
    hideShare: function () {
      this.showShareWishlistModal = false;
    },
    wishlistDeleted: function (wishlistName) {
      this.$parent.wishlistDeleted(wishlistName);
    },
  },
};
</script>